import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import type {
  ContextMenuItem,
  ContextMenuParams,
  DataTableRowReorder,
} from '@core/components';
import { useCrudListActions } from './useCrudListActions';
import {
  type ListFilterConfig,
  applyListFilter,
  useListFilter,
} from './useListFilter';

export type CrudListFilterConfig<T, F> = ListFilterConfig<F> & {
  match?: (item: T, filters: F) => boolean;
};

export interface UseCrudListOptions<T, F = undefined> {
  loadItems: () => Promise<T[]>;
  deleteItem?: (item: T) => Promise<void>;
  loadErrorMessage: string;
  deleteSuccessMessage?: string;
  deleteErrorMessage?: string;
  refreshContext?: () => Promise<void> | void;
  getSearchHaystack?: (item: T) => string;
  filter?: CrudListFilterConfig<T, F>;
  reorderItems?: (ids: number[]) => Promise<void>;
  reorderErrorMessage?: string;
  disabled?: boolean;
}

type UseCrudListSearchReturn = {
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
};

type UseCrudListFilterReturn<F> = {
  filters: F;
  setFilters: Dispatch<SetStateAction<F>>;
  clearFilters: () => void;
  filterOpen: boolean;
  openFilters: () => void;
  closeFilters: () => void;
  activeFilterCount: number;
};

type UseCrudListBaseReturn<T> = UseCrudListSearchReturn & {
  items: T[];
  setItems: Dispatch<SetStateAction<T[]>>;
  filteredItems: T[];
  loading: boolean;
  dialogOpen: boolean;
  editingItem: T | null;
  load: () => Promise<void>;
  openCreate: () => void;
  openEdit: (item: T) => void;
  closeDialog: () => void;
  deleteConfirm: T | null;
  requestDelete: (item: T) => void;
  cancelDelete: () => void;
  confirmDelete: () => Promise<void>;
  deleteLoading: boolean;
  getRowId: (item: T) => string;
  findByRowId: (rowId: string) => T | undefined;
  contextMenuItems: (params: ContextMenuParams) => ContextMenuItem[];
  rowReorder?: DataTableRowReorder;
};

export type UseCrudListReturn<T, F> = UseCrudListBaseReturn<T> &
  (F extends undefined ? object : UseCrudListFilterReturn<F>);

export function useCrudList<T extends { id: number }, F = undefined>(
  options: UseCrudListOptions<T, F>
): UseCrudListReturn<T, F> {
  const {
    loadItems,
    deleteItem,
    loadErrorMessage,
    deleteSuccessMessage,
    deleteErrorMessage,
    refreshContext,
    getSearchHaystack,
    filter,
    reorderItems,
    reorderErrorMessage = 'Failed to reorder items',
    disabled = false,
  } = options;

  const isInitialLoad = useRef(true);

  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    filters,
    setFilters,
    clearFilters,
    filterOpen,
    openFilters,
    closeFilters,
    activeFilterCount,
  } = useListFilter({
    filter,
    searchTerm,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await loadItems();
      setItems(data);

      if (refreshContext) {
        if (isInitialLoad.current) {
          isInitialLoad.current = false;
        } else {
          await refreshContext();
        }
      }
    } catch {
      toast.error(loadErrorMessage);
    } finally {
      setLoading(false);
    }
  }, [loadItems, loadErrorMessage, refreshContext]);

  useEffect(() => {
    if (disabled) {
      setItems([]);
      setLoading(false);
      return;
    }
    load();
  }, [load, disabled]);

  const filteredItems = useMemo(() => {
    let result = items;
    if (getSearchHaystack) {
      const term = searchTerm.trim().toLowerCase();
      if (term) {
        result = result.filter((item) =>
          getSearchHaystack(item).toLowerCase().includes(term)
        );
      }
    }

    return applyListFilter(result, filters, filter?.match);
  }, [items, searchTerm, getSearchHaystack, filters, filter]);

  const {
    dialogOpen,
    editingItem,
    openCreate,
    openEdit,
    closeDialog,
    deleteConfirm,
    requestDelete,
    cancelDelete,
    confirmDelete,
    deleteLoading,
    getRowId,
    findByRowId,
    contextMenuItems,
  } = useCrudListActions({
    items,
    load,
    deleteItem,
    deleteSuccessMessage,
    deleteErrorMessage,
  });

  const hasActiveSearch = searchTerm.trim().length > 0;
  const hasActiveFilter = activeFilterCount > 0;
  const reorderEnabled = Boolean(
    reorderItems && !hasActiveSearch && !hasActiveFilter
  );

  const handleReorder = useCallback(
    async (orderedIds: string[]) => {
      if (!reorderItems) {
        return;
      }

      const previousItems = items;
      const idToItem = new Map(items.map((item) => [String(item.id), item]));
      const nextItems = orderedIds
        .map((id) => idToItem.get(id))
        .filter((item): item is T => item != null);

      if (nextItems.length !== items.length) {
        return;
      }

      setItems(nextItems);

      try {
        await reorderItems(orderedIds.map((id) => Number(id)));
        if (refreshContext) {
          await refreshContext();
        }
      } catch {
        setItems(previousItems);
        toast.error(reorderErrorMessage);
      }
    },
    [items, reorderItems, refreshContext, reorderErrorMessage]
  );

  const rowReorder = useMemo((): DataTableRowReorder | undefined => {
    if (!reorderEnabled) {
      return undefined;
    }
    return { onReorder: handleReorder };
  }, [reorderEnabled, handleReorder]);

  return {
    items,
    setItems,
    filteredItems,
    loading,
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    clearFilters,
    filterOpen,
    openFilters,
    closeFilters,
    activeFilterCount,
    dialogOpen,
    editingItem,
    load,
    openCreate,
    openEdit,
    closeDialog,
    deleteConfirm,
    requestDelete,
    cancelDelete,
    confirmDelete,
    deleteLoading,
    getRowId,
    findByRowId,
    contextMenuItems,
    rowReorder,
  } as unknown as UseCrudListReturn<T, F>;
}
